"use client";

import { useState } from "react";
import { cn } from "@ui/lib";
import { LocationInput } from "./LocationInput";
import type { LocationData } from "./types";

interface LocationInputWithToggleProps {
  value?: LocationData;
  onChange: (location: LocationData | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Initial granularity. When the persisted `value` is country-only (no city,
   * no region), the toggle starts on Country regardless of this prop.
   */
  defaultType?: "city" | "country";
}

/**
 * Platform-only wrapper around the shared LocationInput that adds a small
 * City/Country segmented control above the input. When the user switches to
 * Country, the underlying Geoapify autocomplete re-instantiates with
 * `type="country"`, persisting only the `country` field on LocationData
 * (city/region are cleared).
 *
 * The bare LocationInput is intentionally left unchanged so the marketing
 * AI-credit-report and prompt-generator flows keep their current city-only
 * behavior.
 */
export function LocationInputWithToggle({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  defaultType = "city",
}: LocationInputWithToggleProps) {
  // If we were handed a country-only value, start on the Country tab.
  const initialType: "city" | "country" =
    value && !value.city && !value.region && value.country ? "country" : defaultType;
  const [type, setType] = useState<"city" | "country">(initialType);

  const handleTypeChange = (nextType: "city" | "country") => {
    if (nextType === type) return;
    setType(nextType);
    // Switching granularity invalidates the prior selection — the country
    // result has different shape than a city result. Clear the persisted
    // value so the user is forced to re-pick at the new level.
    onChange(undefined);
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="inline-flex items-center gap-1 rounded-md border border-input bg-background p-0.5 text-xs w-fit"
        role="radiogroup"
        aria-label="Location granularity"
      >
        {(["city", "country"] as const).map((option) => {
          const selected = type === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => handleTypeChange(option)}
              className={cn(
                "rounded px-2.5 py-1 transition-colors capitalize",
                selected
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
      <LocationInput
        value={value}
        onChange={onChange}
        placeholder={
          placeholder ?? (type === "country" ? "Select a country…" : "Select a city…")
        }
        disabled={disabled}
        type={type}
      />
    </div>
  );
}
